import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInvalidate } from '@refinedev/core';
import { Form, Input, Checkbox, Button, Tag, Alert, Modal, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import type { AxiosError } from 'axios';
import { authClient } from '../../providers/axios';
import { isDefaultForEveryUser } from './utils/systemRoles';
import { PermissionPicker } from './components/PermissionPicker';

export interface SystemRole {
  id: string;
  name: string;
  description?: string;
  scope?: 'internal' | 'customer';
  permissions: string[];
  /** Permissions set in code. Older auth-service versions don't send this. */
  seedPermissions?: string[];
}

export interface CatalogPermission {
  key: string;
  description?: string;
  category?: string;
}

interface FormValues {
  description?: string;
  permissions: string[];
}

interface Props {
  role: SystemRole;
  permissions: CatalogPermission[];
  permissionsLoading: boolean;
  /** The permission list could not be loaded: say so rather than show only the role's own keys. */
  permissionsError?: boolean;
}

/**
 * Edit form for a built-in role. The permissions set in code are the starting
 * set: they show ticked and locked (the server refuses to remove them anyway).
 * Admins can add or remove any other permission and edit the description.
 * Name and scope come from code and can't be changed.
 */
export const SystemRoleEditForm: React.FC<Props> = ({
  role,
  permissions,
  permissionsLoading,
  permissionsError = false,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const invalidate = useInvalidate();
  const [submitting, setSubmitting] = useState(false);
  // Values waiting on the "give this to every user?" confirmation.
  const [pending, setPending] = useState<{ values: FormValues; added: string[] } | null>(null);
  const seed = new Set(role.seedPermissions ?? []);

  // Follow the server's copy (a cached record first, then the fresh one) until
  // the admin changes something; after that a refetch must not wipe their edits.
  // Compared by value, because the parent passes a new role object every render.
  const serverDescription = role.description ?? '';
  const serverPermissionsKey = JSON.stringify(role.permissions ?? []);
  // Set only by the admin's own changes (onValuesChange). Not isFieldsTouched():
  // antd marks a field touched when setFieldsValue changes it, which would stop
  // the form following the server after its first update.
  const editedByAdmin = useRef(false);
  useEffect(() => {
    if (editedByAdmin.current) return;
    form.setFieldsValue({
      description: serverDescription,
      permissions: JSON.parse(serverPermissionsKey) as string[],
    });
  }, [form, serverDescription, serverPermissionsKey]);

  // Antd's Checkbox.Group drops any value with no checkbox on the next click,
  // so permissions already on the role but missing from the catalog get a box too.
  const catalogKeys = new Set(permissions.map((p) => p.key));
  const offCatalog = [...new Set([...(role.permissions ?? []), ...seed])]
    .filter((key) => !catalogKeys.has(key))
    .map((key) => ({ key, category: 'Not in the permission list' }));
  const pickerPermissions: CatalogPermission[] = [...permissions, ...offCatalog];

  const save = async (values: FormValues) => {
    setSubmitting(true);
    try {
      await authClient.patch(`/roles/${role.id}`, {
        description: values.description,
        permissions: values.permissions,
      });
      // The page saves with a direct PATCH, so drop Refine's cached copy; otherwise
      // reopening the role shows the values from before this save.
      void invalidate({
        resource: 'roles',
        id: role.id,
        invalidates: ['list', 'detail'],
        dataProviderName: 'authDataProvider',
      });
      message.success('Role updated');
      navigate('/roles');
    } catch (err) {
      const apiMessage = (err as AxiosError<{ error?: { message?: string } }>)?.response?.data
        ?.error?.message;
      message.error(apiMessage || 'Failed to update role');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinish = (values: FormValues) => {
    if (submitting) return;
    const added = values.permissions.filter((p) => !(role.permissions ?? []).includes(p));
    if (isDefaultForEveryUser(role.name) && added.length > 0) {
      setPending({ values, added });
      return;
    }
    void save(values);
  };

  return (
    // The parent keys this form by role id, so a different role gets a fresh form.
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      onValuesChange={() => {
        editedByAdmin.current = true;
      }}
      initialValues={{ description: role.description ?? '', permissions: role.permissions ?? [] }}
    >
      <Alert
        type="info"
        showIcon
        message="Built-in role"
        description="The permissions set in code are always on this role and can't be removed. You can add more, and remove any you added. Users get changes the next time they sign in."
        style={{ marginBottom: 16 }}
      />

      <Form.Item label="Name">
        <Input value={role.name} disabled />
      </Form.Item>

      <Form.Item label="Scope">
        <Tag color={role.scope === 'customer' ? 'orange' : 'purple'}>
          {role.scope ?? 'internal'}
        </Tag>
      </Form.Item>

      <Form.Item
        label="Description"
        name="description"
        rules={[{ required: true, whitespace: true, message: 'Description is required' }]}
      >
        <Input.TextArea rows={3} />
      </Form.Item>

      <Form.Item label="Permissions" name="permissions">
        {permissionsLoading ? (
          <div>Loading permissions...</div>
        ) : permissionsError ? (
          <Alert type="error" showIcon message="Couldn't load the permissions list" />
        ) : (
          <Checkbox.Group style={{ width: '100%' }}>
            <PermissionPicker
              permissions={pickerPermissions}
              isDisabled={(p) => seed.has(p.key)}
              renderExtra={(p) =>
                seed.has(p.key) && (
                  <Tag icon={<LockOutlined />} style={{ marginLeft: 6 }}>
                    Set in code
                  </Tag>
                )
              }
            />
          </Checkbox.Group>
        )}
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={submitting}>
          Save Changes
        </Button>
      </Form.Item>

      <Modal
        open={pending !== null}
        title="Give this to every user?"
        okText="Yes, save"
        confirmLoading={submitting}
        onCancel={() => setPending(null)}
        onOk={async () => {
          if (!pending) return;
          await save(pending.values);
          setPending(null);
        }}
      >
        Every user gets {role.name} when their account is created, so everyone will get:{' '}
        {pending?.added.join(', ')}.
      </Modal>
    </Form>
  );
};
